

import { createBaseService, applyDefaultParams } from './common'


const memoryCache = new Map();

const generateCacheKey = (apiName, params) => {

    const filteredParams = { ...params };
    delete filteredParams.ts;   // 排除时间戳
    delete filteredParams.sign; // 排除签名（如果固定则无需删除）

    const sortedParams = Object.keys(filteredParams).sort().reduce((acc, key) => {
        acc[key] = filteredParams[key];
        return acc;
    }, {});
    return `${apiName}_${JSON.stringify(sortedParams)}`;
};

const cacheResponse = (key, data, ttl = 60) => {
    memoryCache.set(key, {
        data,
        expiresAt: Date.now() + ttl * 1000, // 默认缓存60秒
    });
};

const getCachedResponse = (key) => {
    const cached = memoryCache.get(key);
    if (!cached) return null;
    if (Date.now() > cached.expiresAt) {
        memoryCache.delete(key);
        return null;
    }
    return cached.data;
};


const successCallback = (res) => {
    if (res.result.success) {
        return res.result.data
    }

    let msg = res.result.msg || '请求失败'
    let code = res.result.code || 500;

    // message.error(msg);


    if (res.result.code === 'user_need_login_token_invalid') {
        return Promise.reject({
            code: 'user_need_login_token_invalid',
            msg
        });
    }


    return Promise.reject({
        error: true,
        data: null,
        code,
        msg,
    })

}

export const createClientService = (openLoginHandler) => {
    const instance = createBaseService()

    // 请求拦截器
    instance.interceptors.request.use(config => {
        config = applyDefaultParams(config)

        config.startTime = Date.now(); // 记录请求开始时间




        let mat;
        if (typeof window !== 'undefined') {
            // 客户端：解析document.cookie
            const cookies = document.cookie.split(';').reduce((acc, cookie) => {
                const [key, value] = cookie.trim().split('=').map(k => k.trim());
                acc[key] = decodeURIComponent(value);
                return acc;
            }, {});
            mat = localStorage.getItem('mat')

            if (!mat) {
                mat = cookies.mat;
            }
        }

        if (mat) {
            config.params = config.params || {}; // 确保 params 对象存在
            config.params.mat = mat;
        }

        config.params = {
            ...config.params,
            api: config.apiName // 确保 api 参数存在
        }

        // 仅对 GET 请求启用缓存
        if (config.method?.toLowerCase() === 'get' && config.params.cache !== false) {
            const cacheKey = generateCacheKey(config.apiName, config.params);
            const cachedData = getCachedResponse(cacheKey);
            if (cachedData) {
                // 直接返回缓存数据，终止请求
                return Promise.reject({
                    cached: true,
                    data: cachedData,
                    config,
                });
            }
        }

        // console.log('[client Request]', {
        //     url: config.baseURL + config.url,
        //     api: config.params.api, // 检查API名称是否正确
        //     params: config.params,   // 检查参数是否包含 type=0
        //     signal: config.signal // 确认 signal 存在
        // });
        return config
    })

    // 响应拦截器
    instance.interceptors.response.use(
        response => {

            // 计算耗时
            const endTime = Date.now();
            const duration = endTime - response.config.startTime;
            console.log(`客户端接口 ${response.config.apiName} 成功，耗时 ${duration}ms`);

            if (typeof window !== 'undefined') {
                const { mat, lang } = response.data.meta || {}
                if (mat) localStorage.setItem('mat', mat)
                if (lang) localStorage.setItem('lang', lang)
            }

            // 缓存成功响应
            if (response.config.method?.toLowerCase() === 'get' && response.config.cache !== false) {
                const cacheKey = generateCacheKey(response.config.apiName, response.config.params);
                cacheResponse(cacheKey, response.data, response.config.cacheTTL);
            }

            return successCallback(response.data, () => {
                return Promise.reject({
                    code: 'user_need_login_token_invalid',
                    msg: 'User needs to login'
                });
            });
        },
        error => {
            console.log("🚀 ~ createClientService ~ error:", error)

            // 新增缓存命中处理
            if (error.cached) {
                const endTime = Date.now();
                const duration = endTime - error.config.startTime;
                console.log(`客户端接口 ${error.config.apiName} 缓存命中，耗时 ${duration}ms`);
                return Promise.resolve(error.data?.result?.data); // 关键修改：将缓存数据包装为成功响应
            }

            if (error.config) {
                const endTime = Date.now();
                const duration = endTime - error.config.startTime;
                if (error.cached) {
                    console.log(`客户端接口 ${error.config.apiName} 缓存命中，耗时 ${duration}ms`);
                } else {
                    console.log(`客户端接口 ${error.config.apiName} 失败，耗时 ${duration}ms`);
                }
            }

            const config = error.config || {}
            if (error.response?.status === 401 && config.auth !== false) {
                openLoginHandler?.()
                return Promise.reject({ ...error, _suppressed: true })
            }
            // if (!config.silent) router.push('/error')

            // 处理需要登录的错误
            if (error?.data?.result?.code === 'user_need_login_token_invalid') {
                console.log("🚀 ~ interceptors.response ~ 需要登录:", error.config?.apiName);
                if (config.auth) {
                    openLoginHandler?.(); // 直接触发登录
                    // 对于需要登录的接口，抛出特定错误
                    return Promise.reject({
                        code: 'user_need_login_token_invalid',
                        msg: 'User needs to login',
                        config,
                        _handled: true // 标记已处理
                    });
                }

            }

            return Promise.reject(error)
        }
    )

    return {
        get: (apiName, params, config) => {
            return instance.get('', {
                params: {
                    data: params?.data ? JSON.stringify(params.data) : undefined,
                    ...config,
                },
                apiName,
            })
        },
        post: (apiName, data, config) => {
            const payload = new URLSearchParams();
            payload.append('data', JSON.stringify(data)); // 与服务端结构一致

            return instance.post('', payload, {
                ...config,
                apiName,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    ...config?.headers, // 合并用户自定义 headers
                }
            });
        }
        // instance.post('', data, { ...config, apiName })
    }
}